import api from './api'
import { API_ENDPOINTS, STORAGE_KEYS } from '../utils/constants'
import { KEYCLOAK_ENDPOINTS, KEYCLOAK_CONFIG } from '../config/keycloak'

class AuthService {
  async getCurrentUser() {
    try {
      const response = await api.get(API_ENDPOINTS.CURRENT_USER)
      return response.data
    } catch (error) {
      this.clearAuth()
      throw error
    }
  }

  /**
   * Refresh access token using refresh token
   * @returns {Promise<string>} New access token
   * @throws {Error} If refresh fails
   */
  async refreshAccessToken() {
    try {
      const refreshToken = this.getRefreshToken()
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      const response = await fetch(KEYCLOAK_ENDPOINTS.token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: KEYCLOAK_CONFIG.clientId,
          refresh_token: refreshToken,
        }).toString(),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error_description || 'Token refresh failed')
      }

      const tokens = await response.json()

      // Update stored tokens
      this.setSession({
        token: tokens.access_token,
        refreshToken: tokens.refresh_token,
        idToken: tokens.id_token,
      })

      return tokens.access_token
    } catch (error) {
      // Clear auth on refresh failure
      this.clearAuth()
      throw error
    }
  }

  /**
   * Get token expiration time in milliseconds
   * @returns {number|null} Expiration time or null if invalid
   */
  getTokenExpirationTime() {
    const token = this.getAuthToken()
    if (!token) return null

    try {
      const parts = token.split('.')
      if (parts.length !== 3) return null

      const payload = JSON.parse(atob(parts[1]))
      return payload.exp * 1000 // Convert to milliseconds
    } catch (e) {
      return null
    }
  }

  /**
   * Check if token is expiring soon
   * @param {number} bufferMs - Buffer time in milliseconds (default: 60 seconds)
   * @returns {boolean} True if token is expiring within buffer time
   */
  isTokenExpiring(bufferMs = 60000) {
    const expTime = this.getTokenExpirationTime()
    if (!expTime) return false
    return expTime - Date.now() < bufferMs
  }

  /**
   * Persist tokens and user info locally
   */
  setSession({ token, refreshToken, idToken, user }) {
    if (token) {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token)
    }
    if (refreshToken) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken)
    }
    if (idToken) {
      localStorage.setItem(STORAGE_KEYS.ID_TOKEN, idToken)
    }
    if (user) {
      this.setUserInfo(user)
    }
  }

  /**
   * Get auth token from localStorage
   */
  getAuthToken() {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
  }

  getRefreshToken() {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
  }

  getIdToken() {
    return localStorage.getItem(STORAGE_KEYS.ID_TOKEN)
  }

  /**
   * Set user info in localStorage
   */
  setUserInfo(user) {
    if (user) {
      localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(user))
    }
  }

  /**
   * Get user info from localStorage
   */
  getUserInfo() {
    const userStr = localStorage.getItem(STORAGE_KEYS.USER_INFO)
    try {
      return userStr ? JSON.parse(userStr) : null
    } catch (error) {
      return null
    }
  }

  /**
   * Clear authentication data
   */
  clearAuth() {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.ID_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.USER_INFO)
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!this.getAuthToken()
  }
}

export default new AuthService()
